import { useQuery } from "@tanstack/react-query";
import { ReactNode, useState } from "react";
import { NavLink } from "react-router-dom";

import { useTranslation } from "@/core/i18n";
import { getOrdersCurrentSummary, getPlatform, getUser } from "api/methods";
import { Icon } from "atoms/icon/Icon";
import AccountNav from "atoms/icon/icons/account_nav.svg";
import Cart from "atoms/icon/icons/cart.svg";
import Search from "atoms/icon/icons/ic_search.svg";
import { useAdonisChat } from "modules/adonis/AdonisChatProvider/AdonisChatProvider";
import AdonisAiIcon from "modules/adonis/assets/ai-icon.svg";
import { getPersonaImage } from "modules/adonis/assets/getPersonaImage";
import { normalizePersona } from "modules/adonis/adonis.helpers";
import { Analytics, AnalyticsEvents } from "modules/analytics";
import {
  AnonymousCartButton,
  setPostLoginReturnTo,
  useIsAnonymousBrowsing,
} from "modules/anonymousBrowsing";
import { LoyaltyTierBadge, useLoyaltyStatus } from "modules/loyalty";
import {
  useIsStandaloneProducts,
  useShopFlowPath,
} from "modules/standaloneProducts/standaloneProductsPaths";

const preloadCart = () =>
  void import("pages/root/cart/CartPage")
    .then(({ prefetchCart }) => prefetchCart())
    .catch(() => undefined);
type TopAction = "search" | "adonis" | "account" | "cart";

/**
 * Search / assistant / account / cart actions.
 *
 * Rendered inside the per-page `TopNav` on phones and inside `DesktopHeader`
 * from the `md` breakpoint up, so the two never fall out of sync. The products-only storefront
 * keeps the cart alone: search, the advisor and the account lead into the rest of the app.
 */
export const TopNavActions = ({ accountAction }: { accountAction?: ReactNode }) => {
  const [pressedAction, setPressedAction] = useState<TopAction | null>(null);
  const pressHandlers = (action: TopAction) => ({
    "onPointerDown": () => setPressedAction(action),
    "onPointerUp": () => setPressedAction(null),
    "onPointerCancel": () => setPressedAction(null),
    "onPointerLeave": () => setPressedAction(null),
    "onTouchStart": () => setPressedAction(action),
    "onTouchEnd": () => setPressedAction(null),
    "onTouchCancel": () => setPressedAction(null),
    "data-pressed": pressedAction === action,
    "className": `transition-opacity duration-75 active:opacity-60 ${pressedAction === action ? "opacity-40" : "opacity-100"}`,
  });
  const { t } = useTranslation();
  const isAnonymousBrowsing = useIsAnonymousBrowsing();
  const isStandaloneProducts = useIsStandaloneProducts();
  const flowPath = useShopFlowPath();
  const {
    hasMessages: hasAdonisChatHistory,
    isLauncherDismissed,
    isEnabled: shouldShowAdonisCTA,
    openChat,
    persona,
  } = useAdonisChat();
  const normalizedPersona = normalizePersona(persona);

  const { data: cartDetails } = useQuery({
    ...getOrdersCurrentSummary.keyed({ includePackageLineItems: false }),
  });

  const { data: platform } = useQuery({ ...getPlatform.keyed() });

  const { data: user } = useQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });

  const loyaltyTier = useLoyaltyStatus({
    programEnabled: platform?.platformSettings?.loyaltyProgramEnabled === true,
    isSignedInPatient: !!user && !user.isGuest && !isAnonymousBrowsing,
  })?.currentTier;

  const cartCount = cartDetails?.numShopDisplayOrderLineItems || 0;
  const isShopOpen = platform?.platformSettings?.marketplaceEnabled || isStandaloneProducts;
  const displayCart = !user?.isGuest && (isShopOpen || cartCount > 0);
  const displayAnonymousCart = isAnonymousBrowsing && isShopOpen;

  const accountLink = user?.isGuest ? "/signin" : "/account";
  const rememberReturnPath = isAnonymousBrowsing
    ? () => setPostLoginReturnTo(globalThis.location.pathname + globalThis.location.search)
    : undefined;

  const trackAdonisViewed = (): void => {
    Analytics.track(AnalyticsEvents.ADONIS_VIEWED, {});
  };

  return (
    <>
      {platform?.platformSettings?.marketplaceEnabled && !isStandaloneProducts && (
        <div className="px-1.5">
          <NavLink {...pressHandlers("search")} to="/search">
            <Icon svg={Search} />
          </NavLink>
        </div>
      )}

      {shouldShowAdonisCTA && !isStandaloneProducts && (
        <div className="relative px-1.5">
          <button
            {...pressHandlers("adonis")}
            onClick={() => {
              trackAdonisViewed();
              openChat();
            }}
          >
            {isLauncherDismissed ? (
              <img
                alt=""
                className="h-7 w-7 rounded-full object-cover"
                src={getPersonaImage(normalizedPersona.name).logo}
              />
            ) : (
              <Icon svg={AdonisAiIcon} size="small" />
            )}
            {hasAdonisChatHistory && (
              <div className="absolute -right-[2px] -top-[2px] inline-flex h-2.5 w-2.5 rounded-full border-2 border-white bg-red text-xs font-bold text-white" />
            )}
          </button>
        </div>
      )}

      {!isStandaloneProducts && (
        <div
          className={accountAction ? "order-last ml-3 border-l border-outline-grey pl-4" : "px-1.5"}
        >
          {accountAction || (
            <NavLink
              {...pressHandlers("account")}
              to={accountLink}
              aria-label={t("topNav.account")}
              onClick={rememberReturnPath}
            >
              <div className="relative">
                <Icon svg={AccountNav} />
                {loyaltyTier && (
                  <span className="absolute -bottom-1 -right-1.5 flex rounded-full bg-white p-px">
                    <LoyaltyTierBadge tier={loyaltyTier} size="xs" />
                  </span>
                )}
              </div>
            </NavLink>
          )}
        </div>
      )}

      {displayAnonymousCart && (
        <div
          {...pressHandlers("cart")}
          onPointerDown={() => {
            setPressedAction("cart");
            preloadCart();
          }}
        >
          <div className="flex gap-3 px-1.5">
            <AnonymousCartButton platformId={platform?.id} />
          </div>
        </div>
      )}

      {displayCart && (
        <div className="flex gap-3 px-1.5">
          <NavLink
            {...pressHandlers("cart")}
            to={flowPath("/cart")}
            onPointerDown={() => {
              setPressedAction("cart");
              preloadCart();
            }}
            onMouseEnter={preloadCart}
            onFocus={preloadCart}
          >
            <div className="relative">
              <Icon svg={Cart} />
              {cartCount > 0 && (
                <div className="absolute -right-2 -top-1 inline-flex h-5 w-5 items-center justify-center rounded-full border-2 border-white bg-brand-color text-xs font-bold text-white">
                  {cartCount}
                </div>
              )}
            </div>
          </NavLink>
        </div>
      )}
    </>
  );
};
